import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Radio,
  RadioGroup,
  Divider,
  Stack,
  Switch
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  CalendarMonth as CalendarIcon,
  AcUnit as FrostIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, API_ENDPOINTS } from '../config/api';
import { useTranslation } from 'react-i18next';

const ALL_DEPTS = '*';

/** Fantasy frozen-lake look for locked months — obvious “not editable”. */
const frozenLakeCardSx = {
  position: 'relative' as const,
  overflow: 'hidden',
  borderWidth: 2,
  borderStyle: 'solid',
  borderColor: '#67e8f9',
  borderRadius: 2,
  color: '#0c4a6e',
  backgroundImage: `
    radial-gradient(ellipse 80% 50% at 15% 0%, rgba(255,255,255,0.95) 0%, transparent 55%),
    radial-gradient(ellipse 60% 40% at 90% 15%, rgba(165,243,252,0.75) 0%, transparent 50%),
    radial-gradient(ellipse 50% 60% at 50% 100%, rgba(56,189,248,0.28) 0%, transparent 55%),
    repeating-linear-gradient(
      125deg,
      transparent 0px,
      transparent 11px,
      rgba(255,255,255,0.14) 11px,
      rgba(255,255,255,0.14) 12px
    ),
    linear-gradient(165deg, #f0f9ff 0%, #e0f2fe 28%, #bae6fd 62%, #7dd3fc 100%)
  `,
  boxShadow: `
    0 0 0 1px rgba(255,255,255,0.85),
    0 0 0 3px rgba(34,211,238,0.45),
    0 0 22px rgba(14,165,233,0.35),
    inset 0 0 28px rgba(186,230,253,0.55),
    inset 0 1px 0 rgba(255,255,255,0.95)
  `,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    backgroundImage: `
      linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.45) 48%, transparent 56%),
      radial-gradient(circle at 30% 70%, rgba(255,255,255,0.35) 0 2px, transparent 3px),
      radial-gradient(circle at 70% 40%, rgba(255,255,255,0.4) 0 1.5px, transparent 3px),
      radial-gradient(circle at 55% 25%, rgba(224,242,254,0.9) 0 1px, transparent 2px)
    `,
    backgroundSize: '200% 100%, auto, auto, auto',
    animation: 'frozenShimmer 7s ease-in-out infinite',
    opacity: 0.85,
    zIndex: 0
  },
  '@keyframes frozenShimmer': {
    '0%, 100%': { backgroundPosition: '0% 0%, center, center, center' },
    '50%': { backgroundPosition: '100% 0%, center, center, center' }
  },
  '& > .MuiCardContent-root': {
    position: 'relative',
    zIndex: 1
  }
};

const frozenLakeCardDarkSx = {
  ...frozenLakeCardSx,
  borderColor: '#22d3ee',
  color: '#e0f2fe',
  backgroundImage: `
    radial-gradient(ellipse 80% 50% at 15% 0%, rgba(165,243,252,0.35) 0%, transparent 55%),
    radial-gradient(ellipse 60% 40% at 90% 15%, rgba(34,211,238,0.25) 0%, transparent 50%),
    radial-gradient(ellipse 50% 60% at 50% 100%, rgba(8,47,73,0.8) 0%, transparent 55%),
    repeating-linear-gradient(
      125deg,
      transparent 0px,
      transparent 11px,
      rgba(103,232,249,0.08) 11px,
      rgba(103,232,249,0.08) 12px
    ),
    linear-gradient(165deg, #0c4a6e 0%, #075985 40%, #0369a1 75%, #0e7490 100%)
  `,
  boxShadow: `
    0 0 0 1px rgba(165,243,252,0.35),
    0 0 0 3px rgba(34,211,238,0.35),
    0 0 28px rgba(6,182,212,0.4),
    inset 0 0 32px rgba(8,145,178,0.45),
    inset 0 1px 0 rgba(165,243,252,0.35)
  `
};

type LockMode = 'unlocked' | 'all' | 'selected';

interface MonthLock {
  id?: string;
  year: number;
  month: number;
  isLocked: boolean;
  lockedBy?: string;
  lockedAt?: string;
  lockReason?: string;
  lockedDepartments?: string[];
  locksAllDepartments?: boolean;
  lockedByUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  schedulingOpen?: boolean;
}

const isAllScope = (depts?: string[]) =>
  !depts || depts.length === 0 || depts.includes(ALL_DEPTS);

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const MonthLockManager: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const { t } = useTranslation();

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthLocks, setMonthLocks] = useState<MonthLock[]>([]);
  const [availableDepartments, setAvailableDepartments] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageMonth, setManageMonth] = useState<number | null>(null);
  const [lockMode, setLockMode] = useState<LockMode>('unlocked');
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [autoCloseDays, setAutoCloseDays] = useState(7);
  const [autoCloseDaysDraft, setAutoCloseDaysDraft] = useState('7');
  const [autoCloseSaving, setAutoCloseSaving] = useState(false);
  const [triggeringAnnouncement, setTriggeringAnnouncement] = useState(false);
  const [messageDraft, setMessageDraft] = useState({
    titleLt: '',
    bodyLt: '',
    titleEn: '',
    bodyEn: ''
  });
  const [messageSaving, setMessageSaving] = useState(false);
  const [autoFreezeEnabled, setAutoFreezeEnabled] = useState(false);
  const [autoFreezeDaysDraft, setAutoFreezeDaysDraft] = useState('5');
  const [autoFreezeDays, setAutoFreezeDays] = useState(5);
  const [autoFreezeSaving, setAutoFreezeSaving] = useState(false);

  const months = useMemo(
    () => [
      { value: 1, label: t('common.january', 'January') },
      { value: 2, label: t('common.february', 'February') },
      { value: 3, label: t('common.march', 'March') },
      { value: 4, label: t('common.april', 'April') },
      { value: 5, label: t('common.may', 'May') },
      { value: 6, label: t('common.june', 'June') },
      { value: 7, label: t('common.july', 'July') },
      { value: 8, label: t('common.august', 'August') },
      { value: 9, label: t('common.september', 'September') },
      { value: 10, label: t('common.october', 'October') },
      { value: 11, label: t('common.november', 'November') },
      { value: 12, label: t('common.december', 'December') }
    ],
    [t]
  );

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 8 }, (_, i) => current - 1 + i);
  }, []);

  const locksByMonth = useMemo(() => {
    const map = new Map<number, MonthLock>();
    monthLocks
      .filter((lock) => lock.year === selectedYear)
      .forEach((lock) => map.set(lock.month, lock));
    return map;
  }, [monthLocks, selectedYear]);

  const getMonthRecord = (month: number): MonthLock => {
    return (
      locksByMonth.get(month) || {
        year: selectedYear,
        month,
        isLocked: false,
        lockedDepartments: [],
        schedulingOpen: false
      }
    );
  };

  const managingRecord = manageMonth != null ? getMonthRecord(manageMonth) : null;

  useEffect(() => {
    if (hasPermission('schedule.manage')) {
      fetchMonthLocks();
      fetchDepartments();
      fetchAutoCloseDays();
      fetchAnnouncementMessage();
      fetchAutoFreezeSettings();
    }
  }, [user, selectedYear]);

  const fetchAutoCloseDays = async () => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.SCHEDULING_AUTO_CLOSE_DAYS), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        const days = Number(data.value) || 7;
        setAutoCloseDays(days);
        setAutoCloseDaysDraft(String(days));
      }
    } catch (err) {
      console.error('Error fetching auto-close days:', err);
    }
  };

  const saveAutoCloseDays = async () => {
    const parsed = parseInt(autoCloseDaysDraft, 10);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > 60) {
      setError(
        t(
          'common.autoCloseDaysInvalid',
          'Enter a number of days between 1 and 60'
        )
      );
      return;
    }
    try {
      setAutoCloseSaving(true);
      setError('');
      const response = await fetch(getApiUrl(API_ENDPOINTS.SCHEDULING_AUTO_CLOSE_DAYS), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ value: parsed })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to save');
      }
      const days = Number(data.value) || parsed;
      setAutoCloseDays(days);
      setAutoCloseDaysDraft(String(days));
      setSuccess(
        t('common.autoCloseDaysSaved', 'Auto-close timing saved ({{days}} days)', {
          days
        })
      );
    } catch (err) {
      console.error('Error saving auto-close days:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setAutoCloseSaving(false);
    }
  };

  const triggerAnnouncementForUsers = async () => {
    try {
      setTriggeringAnnouncement(true);
      setError('');
      const upcoming = new Date();
      upcoming.setMonth(upcoming.getMonth() + 1);
      const response = await fetch(
        getApiUrl(API_ENDPOINTS.SCHEDULING_CLOSE_ANNOUNCEMENT_TRIGGER),
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            year: upcoming.getFullYear(),
            month: upcoming.getMonth() + 1
          })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to trigger announcement');
      }
      setSuccess(
        t(
          'common.announcementTriggered',
          'Announcement will show to users on next login (scheduling was not closed)'
        )
      );
    } catch (err) {
      console.error('Error triggering announcement:', err);
      setError(err instanceof Error ? err.message : 'Failed to trigger');
    } finally {
      setTriggeringAnnouncement(false);
    }
  };

  const fetchAnnouncementMessage = async () => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.SCHEDULING_CLOSE_ANNOUNCEMENT_MESSAGE), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        const value = data.value || {};
        setMessageDraft({
          titleLt: value.titleLt || '',
          bodyLt: value.bodyLt || '',
          titleEn: value.titleEn || '',
          bodyEn: value.bodyEn || ''
        });
      }
    } catch (err) {
      console.error('Error fetching announcement message:', err);
    }
  };

  const saveAnnouncementMessage = async () => {
    try {
      setMessageSaving(true);
      setError('');
      const response = await fetch(getApiUrl(API_ENDPOINTS.SCHEDULING_CLOSE_ANNOUNCEMENT_MESSAGE), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(messageDraft)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to save');
      }
      const value = data.value || messageDraft;
      setMessageDraft({
        titleLt: value.titleLt || '',
        bodyLt: value.bodyLt || '',
        titleEn: value.titleEn || '',
        bodyEn: value.bodyEn || ''
      });
      setSuccess(
        t('common.announcementMessageSaved', 'Pranešimo tekstas išsaugotas')
      );
    } catch (err) {
      console.error('Error saving announcement message:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setMessageSaving(false);
    }
  };

  const fetchAutoFreezeSettings = async () => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.SCHEDULING_AUTO_FREEZE), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        const enabled = !!data.value?.enabled;
        const days = Number(data.value?.days) || 5;
        setAutoFreezeEnabled(enabled);
        setAutoFreezeDays(days);
        setAutoFreezeDaysDraft(String(days));
      }
    } catch (err) {
      console.error('Error fetching auto-freeze settings:', err);
    }
  };

  const saveAutoFreezeSettings = async () => {
    const parsed = parseInt(autoFreezeDaysDraft, 10);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > 28) {
      setError(
        t(
          'common.autoFreezeDaysInvalid',
          'Įveskite dienų skaičių nuo 1 iki 28'
        )
      );
      return;
    }
    try {
      setAutoFreezeSaving(true);
      setError('');
      const response = await fetch(getApiUrl(API_ENDPOINTS.SCHEDULING_AUTO_FREEZE), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ enabled: autoFreezeEnabled, days: parsed })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to save');
      }
      const enabled = !!data.value?.enabled;
      const days = Number(data.value?.days) || parsed;
      setAutoFreezeEnabled(enabled);
      setAutoFreezeDays(days);
      setAutoFreezeDaysDraft(String(days));
      setSuccess(
        t('common.autoFreezeSettingsSaved', 'Automatinio užšaldymo nustatymai išsaugoti ({{days}} d.)', {
          days
        })
      );
    } catch (err) {
      console.error('Error saving auto-freeze settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setAutoFreezeSaving(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.MONTH_LOCK_DEPARTMENTS), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableDepartments(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const fetchMonthLocks = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${getApiUrl(API_ENDPOINTS.MONTH_LOCKS)}?year=${selectedYear}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMonthLocks(data.data || []);
      } else {
        setError(t('common.failedFetchMonthLocks', 'Failed to fetch month locks'));
      }
    } catch (err) {
      console.error('Error fetching month locks:', err);
      setError(t('common.failedFetchMonthLocks', 'Failed to fetch month locks'));
    } finally {
      setLoading(false);
    }
  };

  const openManageDialog = (month: number) => {
    const record = getMonthRecord(month);
    const depts = record.lockedDepartments || [];
    let mode: LockMode = 'unlocked';
    let selected: string[] = [];

    if (record.isLocked) {
      if (isAllScope(depts) || record.locksAllDepartments) {
        mode = 'all';
      } else {
        mode = 'selected';
        selected = depts.filter((d) => d !== ALL_DEPTS);
      }
    }

    setManageMonth(month);
    setLockMode(mode);
    setSelectedDepartments(selected);
    setReason(record.lockReason || '');
    setManageOpen(true);
  };

  const closeManageDialog = () => {
    setManageOpen(false);
    setManageMonth(null);
    setReason('');
  };

  const postJson = async (endpoint: string, body: Record<string, unknown>) => {
    const response = await fetch(getApiUrl(endpoint), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || t('common.failedLockMonth', 'Failed to lock month'));
    }
    return data;
  };

  const currentLockedSet = (record: MonthLock): { all: boolean; depts: string[] } => {
    if (!record.isLocked) return { all: false, depts: [] };
    const depts = record.lockedDepartments || [];
    if (isAllScope(depts) || record.locksAllDepartments) {
      return { all: true, depts: [] };
    }
    return { all: false, depts: depts.filter((d) => d !== ALL_DEPTS) };
  };

  const handleSaveLockScope = async () => {
    if (manageMonth == null || !managingRecord) return;

    if (lockMode === 'selected' && selectedDepartments.length === 0) {
      setError(t('common.selectDepartmentsToLock', 'Select at least one department'));
      return;
    }

    const year = selectedYear;
    const month = manageMonth;
    const current = currentLockedSet(managingRecord);

    try {
      setSaving(true);
      setError('');

      if (lockMode === 'unlocked') {
        if (managingRecord.isLocked) {
          await postJson(API_ENDPOINTS.MONTH_UNLOCK, {
            year,
            month,
            departments: [ALL_DEPTS]
          });
        }
      } else if (lockMode === 'all') {
        await postJson(API_ENDPOINTS.MONTH_LOCK, {
          year,
          month,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          departments: [ALL_DEPTS]
        });
      } else {
        const desired = [...new Set(selectedDepartments)];
        const reasonPayload = reason.trim() ? { reason: reason.trim() } : {};
        if (current.all) {
          await postJson(API_ENDPOINTS.MONTH_UNLOCK, {
            year,
            month,
            departments: [ALL_DEPTS]
          });
          await postJson(API_ENDPOINTS.MONTH_LOCK, {
            year,
            month,
            ...reasonPayload,
            departments: desired
          });
        } else {
          const currentSet = new Set(current.depts);
          const desiredSet = new Set(desired);
          const toRemove = current.depts.filter((d) => !desiredSet.has(d));
          const toAdd = desired.filter((d) => !currentSet.has(d));

          if (toRemove.length > 0) {
            await postJson(API_ENDPOINTS.MONTH_UNLOCK, {
              year,
              month,
              departments: toRemove
            });
          }
          if (toAdd.length > 0 || (toRemove.length === 0 && desired.length > 0 && !managingRecord.isLocked)) {
            await postJson(API_ENDPOINTS.MONTH_LOCK, {
              year,
              month,
              ...reasonPayload,
              departments: toAdd.length > 0 ? toAdd : desired
            });
          } else if (toRemove.length > 0 && desired.length > 0 && reason.trim()) {
            await postJson(API_ENDPOINTS.MONTH_LOCK, {
              year,
              month,
              reason: reason.trim(),
              departments: desired
            });
          }
        }
      }

      setSuccess(t('common.monthLockSaved', 'Month lock settings saved'));
      closeManageDialog();
      await fetchMonthLocks();
    } catch (err) {
      console.error('Error saving month lock:', err);
      setError(err instanceof Error ? err.message : t('common.failedLockMonth', 'Failed to lock month'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleScheduling = async (open: boolean) => {
    if (manageMonth == null) return;
    try {
      setSaving(true);
      await postJson(
        open ? API_ENDPOINTS.MONTH_OPEN_SCHEDULING : API_ENDPOINTS.MONTH_CLOSE_SCHEDULING,
        { year: selectedYear, month: manageMonth }
      );
      setSuccess(
        open
          ? t('common.schedulingOpened', 'Auto-approval opened')
          : t('common.schedulingClosedMsg', 'Auto-approval closed')
      );
      await fetchMonthLocks();
    } catch (err) {
      console.error('Error toggling scheduling:', err);
      setError(err instanceof Error ? err.message : t('common.failedLockMonth', 'Request failed'));
    } finally {
      setSaving(false);
    }
  };

  const toggleDepartment = (dept: string) => {
    setSelectedDepartments((prev) =>
      prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept]
    );
  };

  const getMonthName = (month: number) =>
    months.find((m) => m.value === month)?.label || String(month);

  const renderLockBadge = (record: MonthLock) => {
    if (!record.isLocked) {
      return (
        <Chip
          size="small"
          icon={<UnlockIcon />}
          label={t('common.monthLockBadgeOpen', 'Open')}
          color="success"
          variant="outlined"
        />
      );
    }
    const depts = (record.lockedDepartments || []).filter((d) => d && d !== ALL_DEPTS);
    const frostChipSx = {
      bgcolor: 'rgba(224, 242, 254, 0.85)',
      borderColor: '#22d3ee',
      color: '#0e7490',
      fontWeight: 600,
      boxShadow: 'inset 0 0 8px rgba(103,232,249,0.45)',
      '& .MuiChip-icon': { color: '#06b6d4' }
    };
    if (isAllScope(record.lockedDepartments) || record.locksAllDepartments) {
      return (
        <Chip
          size="small"
          icon={<FrostIcon />}
          label={t('common.monthLockBadgeFrozen', 'Frozen')}
          variant="outlined"
          sx={frostChipSx}
        />
      );
    }
    return (
      <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
        <Chip
          size="small"
          icon={<FrostIcon />}
          label={t('common.monthLockBadgePartialFrozen', 'Partially frozen')}
          variant="outlined"
          sx={frostChipSx}
        />
        {depts.map((d) => (
          <Chip key={d} size="small" label={d} variant="outlined" sx={frostChipSx} />
        ))}
      </Box>
    );
  };

  const saveButtonLabel = () => {
    if (lockMode === 'unlocked') return t('common.unlockMonthButton', 'Unfreeze month');
    if (lockMode === 'all') return t('common.lockAllDepartmentsButton', 'Freeze all departments');
    return t('common.lockSelectedDepartmentsButton', 'Freeze selected departments');
  };

  if (!hasPermission('schedule.manage')) {
    return (
      <Box p={3}>
        <Alert severity="error">{t('common.noPermissionAccess')}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        {t('common.monthLockManagement')}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        {t(
          'common.monthLockHelp',
          'Locking blocks schedule edits for the chosen departments. Auto-approval is shared for the whole month.'
        )}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('common.year')}</InputLabel>
          <Select
            value={selectedYear}
            label={t('common.year')}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr',
              lg: '1fr 1fr 1fr 1fr'
            },
            gap: 2
          }}
        >
          {months.map((month) => {
            const record = getMonthRecord(month.value);
            const now = new Date();
            const isPastMonth =
              selectedYear < now.getFullYear() ||
              (selectedYear === now.getFullYear() && month.value < now.getMonth() + 1);
            const needsLockWarning = isPastMonth && !record.isLocked;
            const isFrozen = !!record.isLocked;

            return (
              <Card
                key={month.value}
                variant="outlined"
                sx={(theme) => ({
                  height: '100%',
                  ...(needsLockWarning
                    ? {
                        borderWidth: 2,
                        borderStyle: 'solid',
                        borderColor: 'error.main',
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(211, 47, 47, 0.12)'
                            : 'rgba(211, 47, 47, 0.06)'
                      }
                    : isFrozen
                      ? theme.palette.mode === 'dark'
                        ? frozenLakeCardDarkSx
                        : frozenLakeCardSx
                      : {
                          borderWidth: 1,
                          borderStyle: 'solid',
                          borderColor: 'divider',
                          backgroundColor: 'background.paper'
                        })
                })}
              >
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {isFrozen && !needsLockWarning ? (
                      <FrostIcon sx={{ color: '#0891b2', filter: 'drop-shadow(0 0 4px rgba(34,211,238,0.8))' }} fontSize="small" />
                    ) : (
                      <CalendarIcon color={needsLockWarning ? 'error' : 'action'} fontSize="small" />
                    )}
                    <Typography
                      variant="h6"
                      component="div"
                      sx={(theme) =>
                        needsLockWarning
                          ? { color: 'error.main' }
                          : isFrozen
                            ? {
                                color: theme.palette.mode === 'dark' ? '#e0f2fe' : '#0e7490',
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                                textShadow:
                                  theme.palette.mode === 'dark'
                                    ? '0 0 14px rgba(34,211,238,0.85)'
                                    : '0 0 12px rgba(165,243,252,0.9), 0 1px 0 rgba(255,255,255,0.8)'
                              }
                            : {}
                      }
                    >
                      {month.label}
                    </Typography>
                    {isFrozen && !needsLockWarning && (
                      <FrostIcon
                        sx={{
                          ml: 'auto',
                          color: '#67e8f9',
                          opacity: 0.85,
                          filter: 'drop-shadow(0 0 6px rgba(34,211,238,0.9))'
                        }}
                        fontSize="small"
                      />
                    )}
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    {renderLockBadge(record)}
                    <Chip
                      size="small"
                      icon={record.schedulingOpen ? <UnlockIcon /> : <LockIcon />}
                      label={
                        record.schedulingOpen
                          ? t('common.autoApprovalOpenShort', 'Auto-approve on')
                          : t('common.autoApprovalClosedShort', 'Auto-approve off')
                      }
                      color={record.schedulingOpen ? 'success' : 'default'}
                      variant="outlined"
                      sx={
                        isFrozen && !needsLockWarning
                          ? { bgcolor: 'rgba(255,255,255,0.55)', borderColor: 'rgba(14,116,144,0.35)' }
                          : undefined
                      }
                    />
                  </Stack>

                  {needsLockWarning && (
                    <Typography variant="caption" color="error">
                      {t('common.pastMonthUnlockedWarning', 'Past month is not frozen')}
                    </Typography>
                  )}

                  {isFrozen && !needsLockWarning && (
                    <Typography
                      variant="caption"
                      sx={(theme) => ({
                        color: theme.palette.mode === 'dark' ? '#a5f3fc' : '#0e7490',
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        opacity: 0.9
                      })}
                    >
                      {t('common.monthFrozenNotEditable', 'Frozen — not editable')}
                    </Typography>
                  )}

                  {record.isLocked && record.lockedByUser && (
                    <Typography
                      variant="caption"
                      sx={(theme) => ({
                        color: isFrozen
                          ? theme.palette.mode === 'dark'
                            ? '#bae6fd'
                            : '#155e75'
                          : 'text.secondary'
                      })}
                    >
                      {t('common.lockedBy')}: {record.lockedByUser.firstName}{' '}
                      {record.lockedByUser.lastName}
                    </Typography>
                  )}

                  <Box flexGrow={1} />

                  <Button
                    variant="contained"
                    size="small"
                    color={needsLockWarning ? 'error' : isFrozen ? 'info' : 'primary'}
                    onClick={() => openManageDialog(month.value)}
                    disabled={saving}
                    sx={
                      isFrozen && !needsLockWarning
                        ? {
                            background: 'linear-gradient(135deg, #22d3ee 0%, #0284c7 55%, #0e7490 100%)',
                            boxShadow: '0 0 14px rgba(34,211,238,0.55)',
                            fontWeight: 700,
                            '&:hover': {
                              background: 'linear-gradient(135deg, #67e8f9 0%, #0ea5e9 55%, #0369a1 100%)'
                            }
                          }
                        : undefined
                    }
                  >
                    {t('common.manageMonthLock', 'Manage')}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      <Card variant="outlined" sx={{ mt: 3, mb: 3, maxWidth: 720 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('common.autoCloseSchedulingTitle', 'Automatinis patvirtinimo išjungimas')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'common.autoCloseSchedulingHelp',
              'Sistema kasdien tikrina artėjantį mėnesį. Jei liko ≤ N kalendorinių dienų iki to mėnesio pradžios ir automatinis patvirtinimas dar įjungtas — jis išjungiamas automatiškai, o vartotojams rodomas vienkartinis prisijungimo pranešimas. Rankinis „Išjungti“ pranešimo nerodo.'
            )}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} flexWrap="wrap">
            <TextField
              size="small"
              type="number"
              label={t(
                'common.autoCloseDaysBeforeMonth',
                'Autom. išjungti prieš mėnesį (dienos)'
              )}
              value={autoCloseDaysDraft}
              onChange={(e) => setAutoCloseDaysDraft(e.target.value)}
              inputProps={{ min: 1, max: 60 }}
              sx={{ width: 280 }}
              helperText={t(
                'common.autoCloseDaysCurrent',
                'Dabar: {{days}} d. (numatyta 7)',
                { days: autoCloseDays }
              )}
            />
            <Button
              variant="contained"
              onClick={saveAutoCloseDays}
              disabled={autoCloseSaving}
            >
              {autoCloseSaving
                ? t('common.saving', 'Saugoma…')
                : t('common.save', 'Išsaugoti')}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={triggerAnnouncementForUsers}
              disabled={triggeringAnnouncement}
            >
              {triggeringAnnouncement
                ? t('common.working', 'Vykdoma…')
                : t(
                    'common.showAnnouncementToUsersTest',
                    'Rodyti pranešimą vartotojams (testas)'
                  )}
            </Button>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('common.announcementMessageTitle', 'Pranešimo tekstas')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'common.announcementMessageHelp',
              'Šis tekstas rodomas vartotojams, kai automatinis patvirtinimas išjungiamas. Galite naudoti {{yearToken}}, {{monthToken}} ir {{monthLabelToken}}. Tušti laukai pakeičiami numatytuoju tekstu. Išsaugojimas neparodo pranešimo iš naujo — tam naudokite testo mygtuką.',
              {
                yearToken: '{{year}}',
                monthToken: '{{month}}',
                monthLabelToken: '{{monthLabel}}'
              }
            )}
          </Typography>
          <Stack spacing={2}>
            <TextField
              size="small"
              label={t('common.announcementTitleLt', 'Antraštė (LT)')}
              value={messageDraft.titleLt}
              onChange={(e) => setMessageDraft((prev) => ({ ...prev, titleLt: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label={t('common.announcementBodyLt', 'Tekstas (LT)')}
              value={messageDraft.bodyLt}
              onChange={(e) => setMessageDraft((prev) => ({ ...prev, bodyLt: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              size="small"
              label={t('common.announcementTitleEn', 'Antraštė (EN)')}
              value={messageDraft.titleEn}
              onChange={(e) => setMessageDraft((prev) => ({ ...prev, titleEn: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label={t('common.announcementBodyEn', 'Tekstas (EN)')}
              value={messageDraft.bodyEn}
              onChange={(e) => setMessageDraft((prev) => ({ ...prev, bodyEn: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
            />
            <Box>
              <Button
                variant="contained"
                onClick={saveAnnouncementMessage}
                disabled={messageSaving}
              >
                {messageSaving
                  ? t('common.saving', 'Saugoma…')
                  : t('common.saveAnnouncementMessage', 'Išsaugoti pranešimo tekstą')}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3, maxWidth: 720 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('common.autoFreezeCurrentMonthTitle', 'Einamo mėnesio automatinis užšaldymas')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'common.autoFreezeCurrentMonthHelp',
              'Jei įjungta, sistema kasdien ~06:30 (Vilniaus laiku) tikrina einamą mėnesį. Likus ≤ N kalendorinių dienų iki mėnesio pabaigos, darbo grafikas užšaldomas visiems skyriams ir pakeitimai nebeleidžiami. Jei administratorius atšildo per šį langą, kitą dieną sistema užšaldys vėl.'
            )}
          </Typography>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoFreezeEnabled}
                  onChange={(e) => setAutoFreezeEnabled(e.target.checked)}
                />
              }
              label={t(
                'common.autoFreezeCurrentMonthSwitch',
                'Užšaldyti einamo mėnesio darbo grafiką'
              )}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} flexWrap="wrap">
              <TextField
                size="small"
                type="number"
                label={t(
                  'common.autoFreezeDaysBeforeMonthEnd',
                  'Užšaldyti likus dienoms iki mėnesio pabaigos'
                )}
                value={autoFreezeDaysDraft}
                onChange={(e) => setAutoFreezeDaysDraft(e.target.value)}
                inputProps={{ min: 1, max: 28 }}
                sx={{ width: 320 }}
                helperText={t(
                  'common.autoFreezeDaysCurrent',
                  'Dabar: {{days}} d. (numatyta 5)',
                  { days: autoFreezeDays }
                )}
              />
              <Button
                variant="contained"
                onClick={saveAutoFreezeSettings}
                disabled={autoFreezeSaving}
              >
                {autoFreezeSaving
                  ? t('common.saving', 'Saugoma…')
                  : t('common.save', 'Išsaugoti')}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={manageOpen} onClose={closeManageDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('common.manageMonthLockTitle', 'Manage {{month}} {{year}}', {
            month: manageMonth != null ? getMonthName(manageMonth) : '',
            year: selectedYear
          })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
            {t('common.lockScope', 'Lock scope')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t(
              'common.monthLockScopeHelp',
              'Choose whether the month is open, locked for everyone, or locked only for selected departments.'
            )}
          </Typography>

          <RadioGroup
            value={lockMode}
            onChange={(e) => {
              const next = e.target.value as LockMode;
              setLockMode(next);
              if (next !== 'selected') {
                setSelectedDepartments([]);
              } else if (selectedDepartments.length === 0 && managingRecord?.isLocked) {
                const depts = managingRecord.lockedDepartments || [];
                if (!isAllScope(depts)) {
                  setSelectedDepartments(depts.filter((d) => d !== ALL_DEPTS));
                }
              }
            }}
          >
            <FormControlLabel
              value="unlocked"
              control={<Radio />}
              label={t('common.monthLockModeUnlocked', 'Unlocked — schedule edits allowed')}
            />
            <FormControlLabel
              value="all"
              control={<Radio />}
              label={t('common.monthLockModeAll', 'Lock all departments')}
            />
            <FormControlLabel
              value="selected"
              control={<Radio />}
              label={t('common.monthLockModeSelected', 'Lock selected departments')}
            />
          </RadioGroup>

          {lockMode === 'selected' && (
            <Box sx={{ mt: 1, ml: 1 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button
                  size="small"
                  onClick={() => setSelectedDepartments([...availableDepartments])}
                >
                  {t('common.selectAllDepartments', 'Select all')}
                </Button>
                <Button size="small" onClick={() => setSelectedDepartments([])}>
                  {t('common.clearDepartmentSelection', 'Clear')}
                </Button>
              </Stack>
              <FormGroup sx={{ maxHeight: 220, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                {availableDepartments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('common.noDepartmentsFound', 'No departments found')}
                  </Typography>
                ) : (
                  availableDepartments.map((dept) => (
                    <FormControlLabel
                      key={dept}
                      control={
                        <Checkbox
                          checked={selectedDepartments.includes(dept)}
                          onChange={() => toggleDepartment(dept)}
                        />
                      }
                      label={dept}
                    />
                  ))
                )}
              </FormGroup>
              {selectedDepartments.length === 0 && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {t('common.selectDepartmentsToLock', 'Select at least one department')}
                </Typography>
              )}
            </Box>
          )}

          {(lockMode === 'all' || lockMode === 'selected') && (
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label={t('common.enterLockReason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              rows={2}
            />
          )}

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="subtitle1" gutterBottom>
            {t('common.autoApprovalStatus')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t(
              'common.autoApprovalMonthWideHelp',
              'Auto-approval applies to the whole month, not per department.'
            )}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              icon={managingRecord?.schedulingOpen ? <UnlockIcon /> : <LockIcon />}
              label={
                managingRecord?.schedulingOpen
                  ? t('common.opened')
                  : t('common.closed')
              }
              color={managingRecord?.schedulingOpen ? 'success' : 'default'}
              variant="outlined"
            />
            {managingRecord?.schedulingOpen ? (
              <Button
                size="small"
                color="warning"
                variant="outlined"
                disabled={saving}
                onClick={() => handleToggleScheduling(false)}
              >
                {t('common.closeAutoApproval', 'Close auto-approval')}
              </Button>
            ) : (
              <Button
                size="small"
                color="success"
                variant="outlined"
                disabled={saving}
                onClick={() => handleToggleScheduling(true)}
              >
                {t('common.openAutoApproval', 'Open auto-approval')}
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeManageDialog} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveLockScope}
            variant="contained"
            color={lockMode === 'unlocked' ? 'warning' : 'primary'}
            disabled={saving || (lockMode === 'selected' && selectedDepartments.length === 0)}
            startIcon={saving ? <CircularProgress size={18} /> : lockMode === 'unlocked' ? <UnlockIcon /> : <LockIcon />}
          >
            {saveButtonLabel()}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonthLockManager;
